import {
  Typography,
  Card,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  useMediaQuery,
  Theme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useRecordContext } from "react-admin";
import AnimatedBlock from "../../../components/Common/AnimatedBlock";
import InfoRow from "../../../components/Common/InfoRow";
import { formatCrypto, formatDateTime, formatEuro } from "../../../utils/paymentFormat";

const Mono = ({ children }: { children: any }) => (
  <Typography component="span" sx={{ fontFamily: "monospace", fontSize: 13, fontWeight: 400, overflowWrap: "anywhere" }}>
    {children}
  </Typography>
);

// The transfers billed on the statement, one line each.
function InfoLineItemsTab() {
  const { t }: { t: any } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));
  const record = useRecordContext();
  const lineItems = record?.lineItems || [];

  if (!record) return null;

  const number = { fontWeight: 700, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" } as const;
  return (
    <Box>
      {!isMobile && (
        <Box pb={2}>
          <Typography variant="h6">
            {t("Line items")}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("The gas station transfers billed on this statement.")}
          </Typography>
        </Box>
      )}

      <AnimatedBlock>
        {lineItems.length === 0 ? (
          <Card>
            <Box p={3}>
              <Typography variant="body2" color="text.secondary">
                {t("No line items on this statement.")}
              </Typography>
            </Box>
          </Card>
        ) : isMobile ? (
          lineItems.map((item) => (
            <Card key={item.id} sx={{ mb: 2 }}>
              <Box px={2} py={1}>
                <InfoRow label={t("Transfer")}>
                  <Mono>{item.transferId}</Mono>
                </InfoRow>
                <InfoRow label={t("Amount")}>
                  {formatCrypto(item.amount)} {item.currency}
                </InfoRow>
                <InfoRow label={t("Fee")}>{formatEuro(item.feeEur)}</InfoRow>
                <InfoRow label={t("Date")}>{formatDateTime(item.dateCreated)}</InfoRow>
              </Box>
            </Card>
          ))
        ) : (
          <Card>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("Transfer")}</TableCell>
                    <TableCell>{t("Currency")}</TableCell>
                    <TableCell align="right">{t("Amount")}</TableCell>
                    <TableCell align="right">{t("Fee")}</TableCell>
                    <TableCell align="right">{t("Date")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lineItems.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Mono>{item.transferId}</Mono>
                      </TableCell>
                      <TableCell>{item.currency}</TableCell>
                      <TableCell align="right" sx={number}>{formatCrypto(item.amount)}</TableCell>
                      <TableCell align="right" sx={number}>{formatEuro(item.feeEur)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{formatDateTime(item.dateCreated)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </AnimatedBlock>
    </Box>
  );
}

export default InfoLineItemsTab;
